/**
 * Shared filtering, sorting, and utility functions for Associates & Crews
 * Used by Associates.tsx, AssociatesDesktop.tsx,  and useWerkerSelection.ts
 */

/**
 * Create a filter function for searching associates by text
 * Searches across: FirstName, LastName, Company, Bio
 * 
 * @param searchText - The search query string
 * @returns Filter function that returns true if associate matches search
 */
export const createAssociateSearchFilter = (searchText: string) => {
  const tokens = (searchText || "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  return (user: any): boolean => {
    if (tokens.length === 0) return true;

    const first = (user?.WerkerFirstName || "").toLowerCase();
    const last = (user?.WerkerLastName || "").toLowerCase();
    const company = (user?.WerkerCompany || "").toLowerCase();
    const bio = (user?.WerkerBio || "").toLowerCase();

    const haystack = `${first} ${last} ${company} ${bio}`;

    return tokens.every((token) => haystack.includes(token));
  };
};

/**
 * Create a filter function for searching crews by text
 * Searches across: name, description
 * 
 * @param searchText - The search query string
 * @returns Filter function that returns true if crew matches search
 */
export const createCrewSearchFilter = (searchText: string) => {
  const tokens = (searchText || "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  return (crew: any): boolean => {
    if (tokens.length === 0) return true;

    const name = (crew?.name || "").toLowerCase();
    const description = (crew?.description || "").toLowerCase();

    const haystack = `${name} ${description}`;

    return tokens.every((token) => haystack.includes(token));
  };
};

/**
 * Segment associate requests into incoming and sent buckets
 * 
 * @param requests - Array of request objects
 * @param requestReceivedStatus - Status value for received requests (e.g., "RequestReceived")
 * @param requestSentStatus - Status value for sent requests (e.g., "RequestSent")
 * @returns Object with incomingRequests and sentRequests arrays
 */
export interface RequestSegments {
  incomingRequests: any[];
  sentRequests: any[];
}

export const segmentAssociateRequests = (
  requests: any[],
  requestReceivedStatus: string,
  requestSentStatus: string
): RequestSegments => {
  const incomingRequests = (requests || []).filter(
    (p: any) => p.AssociateStatus === requestReceivedStatus
  );

  const sentRequests = (requests || []).filter(
    (p: any) => p.AssociateStatus === requestSentStatus
  );

  return { incomingRequests, sentRequests };
};

/**
 * Sort associates alphabetically by first name
 * 
 * @param associates - Array of associate objects
 * @returns New sorted array
 */
export const sortAssociatesByName = (associates: any[]): any[] => {
  return [...(associates || [])].sort((a: any, b: any) =>
    (a.WerkerFirstName || "").localeCompare(b.WerkerFirstName || "")
  );
};

/**
 * Filter and sort associates with a search filter
 * Common pattern used across multiple components
 * 
 * @param associates - Array of associate objects
 * @param searchText - Search query string
 * @returns Filtered and sorted array
 */
export const filterAndSortAssociates = (
  associates: any[],
  searchText: string
): any[] => {
  const matchesSearch = createAssociateSearchFilter(searchText);
  const filtered = (associates || []).filter(matchesSearch);
  return sortAssociatesByName(filtered);
};
